import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useBranch } from '../contexts/BranchContext';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { Heart, User, CalendarBlank, Ruler, ScaleLight } from '@phosphor-icons/react';

const BMI = () => {
  const { activeBranch } = useBranch();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Info
    age: '',
    date: new Date().toISOString().split('T')[0],
    height: '',
    weight: '',
    body_age: '',
    phone: '',
    bp_low: '',
    bp_high: '',
    sugar_low: '',
    sugar_high: '',
    
    // Body Composition - Current values (will be mapped to after/before on submit)
    body_fat_men: '',
    body_fat_women: '',
    visceral_fat: '',
    resting_metabolism: '',
    bmi: '',
    whole_body_fat: '',
    trunk_fat: '',
    arms_fat: '',
    leg_fat: '',
    skeletal_muscle_men: '',
    skeletal_muscle_women: '',
    trunk_skeletal_muscle: '',
    arms_skeletal_muscle: '',
    leg_skeletal_muscle: '',
    
    health_status: ''
  });

  useEffect(() => {
    if (activeBranch) {
      fetchCustomers();
    }
  }, [activeBranch]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get(`/customers?branch_id=${activeBranch.branch_id}`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    setSelectedCustomer(customer);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        phone: customer.phone_number || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    try {
      setLoading(true);
      
      // Send all gender-specific fields separately to backend
      // For backwards compatibility, also send combined body_fat and skeletal_muscle
      const body_fat = formData.body_fat_men || formData.body_fat_women || '';
      const skeletal_muscle = formData.skeletal_muscle_men || formData.skeletal_muscle_women || '';
      
      await api.post('/bmi', {
        customer_id: selectedCustomer.customer_id,
        branch_id: activeBranch.branch_id,
        age: formData.age,
        date: formData.date,
        height: formData.height,
        weight: formData.weight,
        body_age: formData.body_age,
        phone: formData.phone,
        bp_low: formData.bp_low,
        bp_high: formData.bp_high,
        sugar_low: formData.sugar_low,
        sugar_high: formData.sugar_high,
        health_status: formData.health_status,
        // Map current values to "after" fields (representing current/latest measurements)
        body_fat_before: '',
        body_fat_after: body_fat,
        body_fat_men_before: '',
        body_fat_men_after: formData.body_fat_men,
        body_fat_women_before: '',
        body_fat_women_after: formData.body_fat_women,
        visceral_fat_before: '',
        visceral_fat_after: formData.visceral_fat,
        resting_metabolism_before: '',
        resting_metabolism_after: formData.resting_metabolism,
        bmi_before: '',
        bmi_after: formData.bmi,
        whole_body_fat_before: '',
        whole_body_fat_after: formData.whole_body_fat,
        trunk_fat_before: '',
        trunk_fat_after: formData.trunk_fat,
        arms_fat_before: '',
        arms_fat_after: formData.arms_fat,
        leg_fat_before: '',
        leg_fat_after: formData.leg_fat,
        skeletal_muscle_before: '',
        skeletal_muscle_after: skeletal_muscle,
        skeletal_muscle_men_before: '',
        skeletal_muscle_men_after: formData.skeletal_muscle_men,
        skeletal_muscle_women_before: '',
        skeletal_muscle_women_after: formData.skeletal_muscle_women,
        trunk_skeletal_muscle_before: '',
        trunk_skeletal_muscle_after: formData.trunk_skeletal_muscle,
        arms_skeletal_muscle_before: '',
        arms_skeletal_muscle_after: formData.arms_skeletal_muscle,
        leg_skeletal_muscle_before: '',
        leg_skeletal_muscle_after: formData.leg_skeletal_muscle
      });

      toast.success('BMI report saved successfully!');
      
      // Reset form
      setFormData({
        age: '',
        date: new Date().toISOString().split('T')[0],
        height: '',
        weight: '',
        body_age: '',
        phone: '',
        bp_low: '',
        bp_high: '',
        sugar_low: '',
        sugar_high: '',
        body_fat_men: '',
        body_fat_women: '',
        visceral_fat: '',
        resting_metabolism: '',
        bmi: '',
        whole_body_fat: '',
        trunk_fat: '',
        arms_fat: '',
        leg_fat: '',
        skeletal_muscle_men: '',
        skeletal_muscle_women: '',
        trunk_skeletal_muscle: '',
        arms_skeletal_muscle: '',
        leg_skeletal_muscle: '',
        health_status: ''
      });
      setSelectedCustomer(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save BMI report'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Heart size={32} weight="fill" className="text-[#2C4C3B]" />
            <h1 className="text-3xl sm:text-4xl font-bold text-[#2C4C3B]">Body Analysis (BMI)</h1>
          </div>
          <p className="text-[#1C1C1A]/60">Comprehensive body composition analysis and tracking</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Customer Selection */}
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold text-[#2C4C3B] mb-4">Select Customer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer (Reg No)</Label>
                <Select value={selectedCustomer?.customer_id || ''} onValueChange={handleCustomerSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer by registration number" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.reg_no} - {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedCustomer && (
                <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
                  <User size={24} className="text-[#2C4C3B]" />
                  <div>
                    <p className="font-semibold text-[#2C4C3B]">{selectedCustomer.name}</p>
                    <p className="text-sm text-[#1C1C1A]/60">{selectedCustomer.reg_no}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Basic Information */}
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold text-[#2C4C3B] mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="age">Age</Label>
                <Input
                  id="age"
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="Enter age"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Customer Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div>
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.1"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="e.g., 170"
                />
              </div>
              <div>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="e.g., 70"
                />
              </div>
              <div>
                <Label htmlFor="body_age">Your Body Age</Label>
                <Input
                  id="body_age"
                  type="number"
                  value={formData.body_age}
                  onChange={(e) => setFormData({ ...formData, body_age: e.target.value })}
                  placeholder="Body age"
                />
              </div>
              <div>
                <Label htmlFor="health_status">Health Status</Label>
                <Select value={formData.health_status} onValueChange={(value) => setFormData({ ...formData, health_status: value })}>
                  <SelectTrigger id="health_status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="healthy">Healthy</SelectItem>
                    <SelectItem value="under_fat">Under Fat</SelectItem>
                    <SelectItem value="over_fat">Over Fat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Blood Pressure (B.P.)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={formData.bp_low}
                    onChange={(e) => setFormData({ ...formData, bp_low: e.target.value })}
                    placeholder="Low"
                  />
                  <span className="self-center">/</span>
                  <Input
                    type="number"
                    value={formData.bp_high}
                    onChange={(e) => setFormData({ ...formData, bp_high: e.target.value })}
                    placeholder="High"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Sugar Level</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={formData.sugar_low}
                    onChange={(e) => setFormData({ ...formData, sugar_low: e.target.value })}
                    placeholder="Low"
                  />
                  <span className="self-center">/</span>
                  <Input
                    type="number"
                    value={formData.sugar_high}
                    onChange={(e) => setFormData({ ...formData, sugar_high: e.target.value })}
                    placeholder="High"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Body Composition - With Reference Ranges */}
          <Card className="p-4 sm:p-6 mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-[#2C4C3B] mb-4">Body Composition Analysis</h2>
            <p className="text-xs sm:text-sm text-[#1C1C1A]/60 mb-4">Scroll table horizontally on mobile →</p>
            
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="min-w-[640px] w-full border-collapse border">
                <thead>
                  <tr className="bg-gray-300">
                    <th className="border border-gray-400 p-2 sm:p-3 text-left font-bold text-xs sm:text-sm">Body Composition</th>
                    <th className="border border-gray-400 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">- (Low)</th>
                    <th className="border border-gray-400 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">- (Normal)</th>
                    <th className="border border-gray-400 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">+ (Risky)</th>
                    <th className="border border-gray-400 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm">++ (Very Risky)</th>
                    <th className="border border-gray-400 p-2 sm:p-3 text-center font-bold bg-gray-400 text-xs sm:text-sm">Yours</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Body Fat % MEN */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Body Fat % MEN</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">5 - 10</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">10 - 20</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">20 - 25</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">25 - 50</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.body_fat_men}
                        onChange={(e) => setFormData({ ...formData, body_fat_men: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Body Fat % WOMEN */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Body Fat % WOMEN</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">5 - 20</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">20 - 30</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">30 - 35</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">35 - 50</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.body_fat_women}
                        onChange={(e) => setFormData({ ...formData, body_fat_women: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Visceral Fat % */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Visceral Fat %</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">5 - 7</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">10 - 13</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">14 - 15</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.visceral_fat}
                        onChange={(e) => setFormData({ ...formData, visceral_fat: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Resting Metabolism */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Resting Metabolism</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">1000</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">1600</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">1800</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">&gt; 1800</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        value={formData.resting_metabolism}
                        onChange={(e) => setFormData({ ...formData, resting_metabolism: e.target.value })}
                        placeholder="kcal"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* BMI (Obesity) */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">BMI (Obesity)</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">18.5 - 23</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">23 - 25</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">25 - &gt;30</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.bmi}
                        onChange={(e) => setFormData({ ...formData, bmi: e.target.value })}
                        placeholder="BMI"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Second Section Header */}
                  <tr className="bg-gray-300">
                    <td className="border border-gray-400 p-3 font-bold">Body Composition</td>
                    <td className="border border-gray-400 p-3 text-center font-bold">Improve</td>
                    <td className="border border-gray-400 p-3 text-center font-bold">OK</td>
                    <td className="border border-gray-400 p-3 text-center font-bold">Good</td>
                    <td className="border border-gray-400 p-3 text-center font-bold">Very Good</td>
                    <td className="border border-gray-400 p-3 text-center font-bold bg-gray-400">Yours</td>
                  </tr>

                  {/* Whole Body Fat */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Whole Body Fat</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">20-30</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.whole_body_fat}
                        onChange={(e) => setFormData({ ...formData, whole_body_fat: e.target.value })}
                        placeholder="kg"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Trunk Fat % */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Trunk Fat %</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">Men</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">&lt; 15%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">Women</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">&lt; 30%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.trunk_fat}
                        onChange={(e) => setFormData({ ...formData, trunk_fat: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Arms Fat */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Arms Fat</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">15.4%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.arms_fat}
                        onChange={(e) => setFormData({ ...formData, arms_fat: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Leg Fat */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Leg Fat</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">24.8%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.leg_fat}
                        onChange={(e) => setFormData({ ...formData, leg_fat: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Skeletal Muscle MEN */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Skeletal Muscle MEN</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">1 - 35</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">32 - 36</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">36 - 38</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">&gt; 38</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.skeletal_muscle_men}
                        onChange={(e) => setFormData({ ...formData, skeletal_muscle_men: e.target.value })}
                        placeholder="kg"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Skeletal Muscle WOMEN */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Skeletal Muscle WOMEN</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">1 - 26</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">26 - 28</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">28 - 30</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">&gt; 30</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.skeletal_muscle_women}
                        onChange={(e) => setFormData({ ...formData, skeletal_muscle_women: e.target.value })}
                        placeholder="kg"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Trunk Skeletal Muscle */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Trunk Skeletal Muscle</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">26.9%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.trunk_skeletal_muscle}
                        onChange={(e) => setFormData({ ...formData, trunk_skeletal_muscle: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Arms Skeletal Muscle */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Arms Skeletal Muscle</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">24.2%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.arms_skeletal_muscle}
                        onChange={(e) => setFormData({ ...formData, arms_skeletal_muscle: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Leg Skeletal Muscle */}
                  <tr className="bg-gray-100">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Leg Skeletal Muscle</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">38.8%</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.leg_skeletal_muscle}
                        onChange={(e) => setFormData({ ...formData, leg_skeletal_muscle: e.target.value })}
                        placeholder="%"
                        className="text-center text-xs sm:text-sm h-8 sm:h-10"
                      />
                    </td>
                  </tr>

                  {/* Health / Under Fat / Over Fat */}
                  <tr className="bg-gray-200">
                    <td className="border border-gray-400 p-2 sm:p-3 font-medium text-xs sm:text-sm">Health / Under Fat / Over Fat</td>
                    <td colSpan="4" className="border border-gray-400 p-2 sm:p-3 text-center text-xs sm:text-sm">--</td>
                    <td className="border border-gray-400 p-2 sm:p-3 bg-white">
                      <Select value={formData.health_status} onValueChange={(value) => setFormData({ ...formData, health_status: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="healthy">Healthy</SelectItem>
                          <SelectItem value="under_fat">Under Fat</SelectItem>
                          <SelectItem value="over_fat">Over Fat</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormData({
                  age: '',
                  date: new Date().toISOString().split('T')[0],
                  height: '',
                  weight: '',
                  body_age: '',
                  phone: '',
                  bp_low: '',
                  bp_high: '',
                  sugar_low: '',
                  sugar_high: '',
                  body_fat_men: '',
                  body_fat_women: '',
                  visceral_fat: '',
                  resting_metabolism: '',
                  bmi: '',
                  whole_body_fat: '',
                  trunk_fat: '',
                  arms_fat: '',
                  leg_fat: '',
                  skeletal_muscle_men: '',
                  skeletal_muscle_women: '',
                  trunk_skeletal_muscle: '',
                  arms_skeletal_muscle: '',
                  leg_skeletal_muscle: '',
                  health_status: ''
                });
                setSelectedCustomer(null);
              }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedCustomer}
              className="bg-[#2C4C3B] hover:bg-[#1F3329]"
            >
              {loading ? 'Saving...' : 'Save BMI Report'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default BMI;
